import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { NgScrollbar } from 'ngx-scrollbar';
import { trigger, transition, style, animate } from '@angular/animations';
import { TranslateModule } from '@ngx-translate/core';

export interface Notifications {
  title?: string;
  message: string;
  time: string;
  icon?: string;
  userImg?: string;
  color: string;
  status: string;
  id?: string; // Optional unique identifier for each notification
  actionLabel?: string; // Optional action button label
  actionType?: string; // Optional action type (e.g., 'View', 'Reply', 'Mark as Important')
}

@Component({
  selector: 'app-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss'],
  imports: [
    MatMenuModule,
    NgScrollbar,
    FeatherIconsComponent,
    CommonModule,
    MatButtonModule,
    TranslateModule,
  ],
  animations: [
    trigger('notificationAnimation', [
      transition(':leave', [
        animate(
          '0.5s ease-out',
          style({ opacity: 0, transform: 'translateX(30px)' })
        ),
      ]),
    ]),
  ],
})
export class NotificationListComponent implements OnInit, OnChanges {
  @Input() notifications: Notifications[] = [];
  @Input() titleText = '';
  @Input() markAllLabel = '';
  @Input() footerLabel = '';
  @Input() emptyLabel = '';
  @Input() triggerIcon = 'notifications_active';
  @Input() showClose = true;
  @Input() showMarkAll = true;
  @Output() markAllAsRead = new EventEmitter<void>();
  @Output() readAll = new EventEmitter<void>();
  @Output() closeNotification = new EventEmitter<Notifications>();
  @Output() notificationRead = new EventEmitter<Notifications>();
  @Output() actionClick = new EventEmitter<{
    notification: Notifications;
    actionType: string;
  }>();

  // Track notifications being removed for animation
  removingNotification: Notifications | null = null;

  // Count of unread notifications
  unreadCount = 0;

  get resolvedTitle(): string {
    return this.titleText || 'notifications.title';
  }

  get resolvedMarkAllLabel(): string {
    return this.markAllLabel || 'notifications.markAllRead';
  }

  get resolvedFooterLabel(): string {
    return this.footerLabel || 'notifications.readAll';
  }

  get resolvedEmptyLabel(): string {
    return this.emptyLabel || 'notifications.empty';
  }

  ngOnInit() {
    this.updateUnreadCount();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['notifications']) {
      this.updateUnreadCount();
    }
  }

  // Update the unread count based on notification status
  updateUnreadCount() {
    this.unreadCount = this.notifications.filter(
      (n) => n.status === 'msg-unread'
    ).length;
  }

  markAll() {
    this.markAllAsRead.emit();
    // Update all notifications to read status
    this.notifications.forEach((notification) => {
      notification.status = 'msg-read';
    });
    this.updateUnreadCount();
  }

  readAllNotifications() {
    this.readAll.emit();
    this.updateUnreadCount();
  }

  removeNotification(notification: Notifications) {
    // Set the notification as being removed for animation
    this.removingNotification = notification;

    // Mark as read first
    if (notification.status === 'msg-unread') {
      notification.status = 'msg-read';
      this.updateUnreadCount();
    }

    // Emit after a short delay to allow animation to complete
    setTimeout(() => {
      this.closeNotification.emit(notification);
      this.removingNotification = null;
    }, 500);
  }

  // Check if a notification is currently being removed
  isRemoving(notification: Notifications): boolean {
    return this.removingNotification === notification;
  }

  // Mark notification as read when clicked
  markAsRead(notification: Notifications): void {
    if (notification.status === 'msg-unread') {
      notification.status = 'msg-read';
      this.updateUnreadCount();
      this.notificationRead.emit(notification);
    }
  }

  // Handle action button click
  onActionClick(notification: Notifications) {
    this.actionClick.emit({
      notification,
      actionType: notification.actionType || 'default',
    });

    // If the notification is unread, mark it as read when action is clicked
    if (notification.status === 'msg-unread') {
      notification.status = 'msg-read';
      this.updateUnreadCount();
      this.notificationRead.emit(notification);
    }
  }
}
